/**
 * LADRILHAR EM VOLTA DE UM PONTO — o explorador do item de pavimentação.
 *
 * Um polígono pavimenta o plano sozinho quando o seu ângulo interno cabe um número
 * INTEIRO de vezes em volta de um ponto. A conta é uma divisão; a figura é um leque de
 * peças iguais girando em torno do vértice, e a fresta que sobra quando a divisão não é
 * exata. É a mesma ideia do buraco que fica ao tentar ladrilhar um chão com peças de
 * pentágono.
 *
 * O EXPLORADOR RECEBE UM ÂNGULO, E NÃO UM POLÍGONO. Se recebesse o polígono, bastaria
 * percorrer a lista do item para ter todas as respostas, e a marcação não mediria nada.
 * Recebendo o ângulo — e andando de dez em dez, o que deixa 108° e 135° fora de
 * alcance —, ele ensina a REGRA com os ângulos redondos e deixa o pentágono e o
 * octógono para quem souber aplicá-la.
 *
 * A fresta é desenhada, e não escrita. O tamanho dela em graus seria o veredito; o
 * desenho é a estrutura.
 */

import { adjetivo, capitalizar, conta, numeroDe, verbo } from '../core/pt.ts';
import type { Vec2 } from '../geom/vec.ts';
import { scene, type Scene, type Shape } from './scene.ts';

const R = 100;

const rad = (g: number): number => (g * Math.PI) / 180;
const ponto = (g: number, r = R): Vec2 => ({ x: r * Math.cos(rad(g)), y: r * Math.sin(rad(g)) });

export interface OpcoesLadrilho {
  /** O ângulo de cada peça, em graus. */
  readonly angulo: number;
  readonly title: string;
  readonly desc: string;
}

export function ladrilhoAoRedor(o: OpcoesLadrilho): Scene {
  const cabem = Math.floor(360 / o.angulo);
  const fresta = 360 - cabem * o.angulo;
  const shapes: Shape[] = [];

  // As peças: triângulos iguais com a ponta no vértice comum, das 12 horas em diante,
  // no sentido horário — a ordem em que a mão assentaria ladrilho.
  for (let k = 0; k < cabem; k++) {
    const a0 = 90 - k * o.angulo;
    shapes.push({
      t: 'poly',
      pts: [{ x: 0, y: 0 }, ponto(a0), ponto(a0 - o.angulo)],
      closed: true,
      role: 'given',
      fill: 'given-fill',
    });
  }

  // A fresta, tracejada: o que sobra quando a divisão não é exata.
  if (fresta > 0.5) {
    const a0 = 90 - cabem * o.angulo;
    shapes.push({
      t: 'poly',
      pts: [{ x: 0, y: 0 }, ponto(a0), ponto(a0 - fresta)],
      closed: true,
      role: 'construction',
      fill: 'muted-fill',
    });
  }

  shapes.push({ t: 'point', p: { x: 0, y: 0 }, role: 'given' });

  const m = R * 1.16;
  return scene(shapes, {
    viewBox: { x: -m, y: -m, w: 2 * m, h: 2 * m },
    a11y: { title: o.title, desc: o.desc },
  });
}

/** Descricao textual do explorador -- sem dizer se fecha ou nao. */
export function describeLadrilho(angulo: number): string {
  const cabem = Math.floor(360 / angulo);
  const n = numeroDe(cabem);
  return (
    `Peças iguais de ${conta(angulo, 'grau')}, encostadas umas nas outras em volta de um ponto. ` +
    `${capitalizar(verbo('estar.pres', n))} ${adjetivo('desenhado', 'f', n)} ${conta(cabem, 'peca')}` +
    `${360 - cabem * angulo > 0.5 ? ', e depois delas há uma fresta tracejada' : ', que fecham a volta'}.`
  );
}

/** O angulo interno de um poligono regular, em graus. */
export const anguloInterno = (lados: number): number => ((lados - 2) * 180) / lados;

/** Os vertices de um poligono regular, com um lado na horizontal embaixo. */
export function poligonoRegular(lados: number, centro: Vec2, raio: number): Vec2[] {
  // O giro põe um LADO na base, e não um vértice: um pentágono apoiado num vértice
  // parece outra figura, e um triângulo de ponta para baixo parece um alerta.
  const giro = 270 + 180 / lados;
  return Array.from({ length: lados }, (_v, i) => {
    const g = giro + (i * 360) / lados;
    return { x: centro.x + raio * Math.cos(rad(g)), y: centro.y + raio * Math.sin(rad(g)) };
  });
}
